import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import {
  getCurrencyRatesIsFetching,
  getCurrenciesPairInfoForHooks,
  getCurrenciesInfoWithRates,
  getCurrenciesCrossRatesIsFetching,
  getCurrenciesCrossRatesHasData,
} from 'selectors';
import { useSelectorProps } from 'hooks';
import { getCurrencyCase, getCurrencyCaseByValue } from 'utils';

import ContentSection from 'components/ContentSection';
import ContentPlaceholder from 'components/ContentPlaceholder';

import { Title, ItemsList, Item, SourceLink, TargetLabel } from './styles';

const contentPlaceholderStyles = { height: '16px', minWidth: '200px' };

const LandingLinksBlock = memo(() => {
  const selectorProps = useSelectorProps();
  const { formatNumber } = selectorProps.intl;

  const isFetching = useSelector(state => getCurrencyRatesIsFetching(state));
  const crossRatesIsFetching = useSelector(state =>
    getCurrenciesCrossRatesIsFetching(state, selectorProps)
  );
  const hasCrossRates = useSelector(state =>
    getCurrenciesCrossRatesHasData(state, selectorProps)
  );
  const { source, target } = useSelector(state =>
    getCurrenciesPairInfoForHooks(state, selectorProps)
  );
  const currencies = useSelector(state =>
    getCurrenciesInfoWithRates(state, selectorProps)
  );

  const sourceValue = parseFloat(selectorProps.match.params.val1);
  const showLoading = isFetching || (crossRatesIsFetching && !hasCrossRates);

  return (
    <ContentSection as="section">
      <Title>
        <FormattedMessage
          id="vl.landingLinksBlockTitle"
          values={{
            formattedValue: formatNumber(sourceValue),
            currencyValueCaseS: getCurrencyCaseByValue(
              source,
              target,
              sourceValue
            ).currencyValueCaseS,
          }}
        />
      </Title>

      <ItemsList>
        {showLoading
          ? Array.from(Array(5)).map(index => (
              // eslint-disable-next-line
              <Item key={index}>
                <SourceLink>
                  <ContentPlaceholder style={contentPlaceholderStyles} />
                </SourceLink>

                <TargetLabel>
                  <ContentPlaceholder style={contentPlaceholderStyles} />
                </TargetLabel>
              </Item>
            ))
          : currencies.map(({ rate, ...item }) => {
              const { currencyGpS, currencyGpT } = getCurrencyCase(
                source,
                item,
                'gp'
              );
              const { currencyMnpsT } = getCurrencyCase(source, item, 'mnps');

              const link = `/converter/${sourceValue}-${(
                source.slug || ''
              ).toLowerCase()}-to-${(item.slug || '').toLowerCase()}/`;

              return (
                <Item key={link}>
                  <SourceLink to={link}>
                    {`${formatNumber(
                      sourceValue
                    )} ${currencyGpS} в ${currencyMnpsT}`}
                  </SourceLink>

                  <TargetLabel>
                    {!rate
                      ? '—'
                      : `= ${formatNumber(sourceValue * rate, {
                          maximumFractionDigits:
                            source.type === 'crypto' ? 8 : 2,
                        })} ${currencyGpT}`}
                  </TargetLabel>
                </Item>
              );
            })}
      </ItemsList>
    </ContentSection>
  );
});

export default LandingLinksBlock;
